import type { Customer } from "@/pages/admin/customers/hooks/useCustomers";

interface CustomerStatusModalProps {
  open: boolean;
  customer: Customer | null;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export default function CustomerStatusModal({
  open,
  customer,
  busy,
  onConfirm,
  onCancel,
}: CustomerStatusModalProps) {
  if (!open || !customer) return null;

  const suspending = customer.status !== "suspended";

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8">
      <button
        type="button"
        aria-label="关闭确认弹窗"
        onClick={() => {
          if (!busy) onCancel();
        }}
        className="absolute inset-0 cursor-pointer bg-foreground-950/50"
      />

      <div
        role="dialog"
        aria-modal="true"
        className="lh-fade-up relative z-10 flex w-full max-w-md flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50"
      >
        <div className="flex items-start gap-3 border-b border-background-200 px-5 py-4">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${
              suspending ? "bg-accent-100 text-accent-700" : "bg-primary-100 text-primary-700"
            }`}
          >
            <i className={`${suspending ? "ri-user-forbid-line" : "ri-user-follow-line"} text-xl`} />
          </span>
          <div className="flex-1">
            <h2 className="font-heading text-base font-semibold text-foreground-950">
              {suspending ? "停用该客户账号" : "恢复该客户账号"}
            </h2>
            <p className="mt-1 text-xs leading-relaxed text-foreground-600">
              {suspending
                ? "停用后该客户将无法访问受保护的数据，已登录的会话也会在下次校验时被拒绝。"
                : "恢复后该客户可以重新登录并管理自己的许可证与申请。"}
            </p>
          </div>
        </div>

        <div className="border-b border-background-200 bg-background-100 px-5 py-3">
          <p className="font-heading text-sm font-semibold text-foreground-950">{customer.name}</p>
          <p className="mt-0.5 text-xs text-foreground-600">{customer.email || "无邮箱"}</p>
          {customer.company && <p className="mt-0.5 text-xs text-foreground-500">{customer.company}</p>}
        </div>

        <div className="flex flex-wrap justify-end gap-2 px-5 py-4">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
          >
            取消
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md px-5 py-2 text-sm font-medium text-background-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              suspending ? "bg-accent-600 hover:bg-accent-700" : "bg-primary-500 hover:bg-primary-600"
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={suspending ? "ri-forbid-2-line" : "ri-refresh-line"} />
            </span>
            {busy ? "处理中…" : suspending ? "确认停用" : "确认恢复"}
          </button>
        </div>
      </div>
    </div>
  );
}